import React, { useState, useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useManualQuery, useMutation } from "graphql-hooks";
import { useNavigate, Link } from "react-router-dom";

import {
  TextField,
  Box,
  Divider,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Avatar,
  Select,
  MenuItem,
} from "@mui/material";

import AlertPopup from "../components/AlertPopup.js";
import { styled } from "@mui/material/styles";

import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowDropDown,
} from "@mui/icons-material";

import placeholderImage from "../assets/r3play-logo-sml.png";

import { ALL_VIDEOS_LIST, DELETE_VIDEO, UPDATE_VIDEO_STATUS } from "../graphQL/video_qry";

// Status Options
const STATUS_OPTIONS = ["All", "Published", "Draft"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

// UserLink Component
const UserLink = ({ username, userUuid }) => (
  <StyledTooltip title="View User's Page">
    <Link
      to={`/user/${userUuid}/profile`}
      target="_blank"
      style={{
        color: "#d32f2f",
        textDecoration: "none",
        fontSize: "14px",
        display: "inline-block",
        lineHeight: 1,
      }}
      onMouseEnter={e => {
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={e => {
        e.target.style.textDecoration = "none";
      }}
    >
      {username}
    </Link>
  </StyledTooltip>
);

const RootDiv = styled("div")({
  marginRight: "8rem",
  textAlign: "center",
  padding: 2,
  paddingLeft: 0,
  paddingRight: 0,
  width: "1400px",
  //  minWidth: "500px",
});

const ContainerDiv = styled("div")({
  padding: 32,
  marginBottom: "5rem",
  clear: "both",
  borderRadius: "8px",
  minHeight: "870px",
  backgroundColor: "#ffffff",
});

export default function AdminToysList() {
  const navigate = useNavigate();
  const [isPaginationMenuOpen, setIsPaginationMenuOpen] = useState(false);
  const paginationSelectRef = useRef(null);
  const paginationMenuRef = useRef(null);
  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    videoId: null,
  });

  const [videos, setVideos] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [rows, setRows] = useState([]);

  // Video Mutation
  const [getAllVideos] = useManualQuery(ALL_VIDEOS_LIST);
  const [deleteVideo] = useMutation(DELETE_VIDEO);
  const [updateVideoStatus] = useMutation(UPDATE_VIDEO_STATUS);

  // Get data from database
  useEffect(() => {
    allVideosData();
  }, []);

  const allVideosData = async () => {
    const {
      loading: loadingAllVideosData,
      error: errorAllVideosData,
      data: dataAllVideosData,
    } = await getAllVideos();
    if (!loadingAllVideosData && dataAllVideosData) {
      console.log(dataAllVideosData);
      const videosData = Object.values(dataAllVideosData.allVideos).map(item => ({
        id: Number(item.videoId),
        uuid: item.uuid,
        videoUrl: item.videoUrl,
        username: item.userId.username,
        useruuid: item.userId.userUuid,
        title: item.title,
        dateCreated: item.createDate,
        status: item.isPublished ? "Published" : "Draft",
        datePublished: item.isPublished ? item.publishedDate : "",
      }));
      setVideos(videosData);
    }
  };

  // Initialize data
  useEffect(() => {
    setRows(getFilteredData);
  }, [statusFilter, videos]);

  const getFilteredData = () => {
    let filteredData = videos;
    if (statusFilter !== "All") {
      if (statusFilter === "Draft") {
        filteredData = filteredData.filter(toy => toy.status === "Draft");
      } else {
        filteredData = filteredData.filter(toy => toy.status === "Published");
      }
    }
    return filteredData;
  };

  // Status filter change handlers
  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
  };

  // Helper function to get video thumbnail from any platform
  const getVideoThumbnail = videoUrl => {
    if (!videoUrl) return "";

    // YouTube
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      let videoId = "";
      if (videoUrl.includes("youtu.be/")) {
        videoId = videoUrl.split("youtu.be/")[1].split("?")[0];
      } else if (videoUrl.includes("youtube.com")) {
        const urlParams = new URLSearchParams(videoUrl.split("?")[1]);
        videoId = urlParams.get("v");
      }
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    // Vimeo
    if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/").pop().split("?")[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    }

    // Dailymotion
    if (videoUrl.includes("dailymotion.com") || videoUrl.includes("dai.ly")) {
      let videoId = "";
      if (videoUrl.includes("dai.ly/")) {
        videoId = videoUrl.split("dai.ly/")[1].split("?")[0];
      } else {
        videoId = videoUrl.split("/video/")[1].split("?")[0];
      }
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    }

    // Default fallback - return empty or a placeholder
    return "";
  };

  const dateFormatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // change to false if you prefer 24h format
  });

  const dayFormat = day => {
    const dayDate = new Date(day);
    return formatter.format(dayDate);
  };

  const dateFormat = date => {
    const dayDate = new Date(date);
    return dateFormatter.format(dayDate);
  };

  const columns = [
    {
      field: "videoImage",
      headerName: "Image",
      minWidth: 5,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={getVideoThumbnail(rows.row.videoUrl) || placeholderImage}
              alt={rows.row.title}
              onError={e => {
                e.target.onerror = null;
                e.target.src = placeholderImage;
              }}
              sx={{
                borderRadius: "8px",
                width: 80,
                height: 80,
                //objectFit: "cover",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "username",
      headerName: "Creator",
      minWidth: 70,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return <UserLink username={rows.row.username} userUuid={rows.row.useruuid} />;
      },
    },
    {
      field: "title",
      headerName: "Title",
      minWidth: 200,
      flex: 2,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: params => (
        <div
          style={{
            whiteSpace: "normal", // ⬅️ allows wrapping
            wordBreak: "break-word",
            lineHeight: "1.2",
            textAlign: "left",
            padding: "0 4px",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "dateCreated",
      headerName: "Date Created",
      minWidth: 90,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <StyledTooltip title={dayFormat(rows.row.dateCreated)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.dateCreated)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "datePublished",
      headerName: "Date Published",
      minWidth: 90,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        if (rows.row.datePublished === "") return "";
        return (
          <StyledTooltip title={dayFormat(rows.row.datePublished)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.datePublished)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 20,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              color: "#333",
              border: "1px solid #000",
              fontSize: "12px",
              fontWeight: "500",
              width: "80px",
              height: "30px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {rows.row.status}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        return (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {rows.row.status === "Draft" ? (
              <StyledTooltip title="Publish this video">
                <IconButton
                  size="small"
                  onClick={() => handlePublishClick(rows.row.id)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <AssignmentTurnedInOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            ) : (
              <StyledTooltip title="Unpublish this video">
                <IconButton
                  size="small"
                  onClick={() => handleUnpublishClick(rows.row.id)}
                  sx={{
                    color: "#666",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <FactCheckOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            )}
            <StyledTooltip title="View this video">
              <IconButton
                component={Link}
                to={`/video/${rows.row.uuid}`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Edit this video">
              <IconButton
                component={Link}
                to={`/video/${rows.row.uuid}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Delete this video">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(rows.row.id)}
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
          </Box>
        );
      },
    },
  ];

  function DataGridTitle() {
    return (
      <>
        <Grid
          container
          xs={12}
          sx={{
            pt: 2,
            pb: 2,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: 20,
              fontWeight: 500,
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              margin: "0 auto",
            }}
          >
            Videos List
          </Typography>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{
              width: "170px",
              paddingRight: 2,
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              },
              "& .MuiOutlinedInput-root": {
                height: "40px",
                backgroundColor: "white",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                fontSize: "14px",
                "&.Mui-focused": {
                  color: "#666",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            SelectProps={{
              IconComponent: ArrowDropDown,
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      justifyContent: "flex-start",
                      textAlign: "left",
                    },
                  },
                },
              },
            }}
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Divider />
      </>
    );
  }

  useEffect(() => {
    const handleScroll = event => {
      if (isPaginationMenuOpen) {
        const isScrollingInsideDropdown =
          (paginationMenuRef.current &&
            paginationMenuRef.current.contains(event.target)) ||
          (paginationSelectRef.current &&
            paginationSelectRef.current.contains(event.target));

        if (!isScrollingInsideDropdown) {
          setIsPaginationMenuOpen(false);
        }
      }
    };

    if (isPaginationMenuOpen) {
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isPaginationMenuOpen]);

  const handleConfirmAction = async () => {
    const videoUuid = videos.find(item => item.id === alertPopup.videoId)?.uuid;

    if (alertPopup.type === "publish") {
      // Change it into database
      const {
        loading: loadingVideoStatus,
        error: errorVideoStatus,
        data: videoStatus,
      } = await updateVideoStatus({
        variables: {
          videoUuid,
          isPublished: true,
        },
      });

      if (!loadingVideoStatus && videoStatus && videoStatus.updateVideoStatus) {
        // Update video to published
        setVideos(prevVideos =>
          prevVideos.map(video =>
            video.id === alertPopup.videoId
              ? {
                  ...video,
                  datePublished: new Date(),
                  status: "Published",
                }
              : video
          )
        );
      } else {
        console.log("errorVideoData: ", errorVideoStatus);
      }
    } else if (alertPopup.type === "unpublish") {
      // Change it into database
      const {
        loading: loadingVideoStatus,
        error: errorVideoStatus,
        data: videoStatus,
      } = await updateVideoStatus({
        variables: {
          videoUuid,
          isPublished: false,
        },
      });
      if (!loadingVideoStatus && videoStatus && videoStatus.updateVideoStatus) {
        // Update video to draft
        setVideos(prevVideos =>
          prevVideos.map(video =>
            video.id === alertPopup.videoId
              ? { ...video, datePublished: "", status: "Draft" }
              : video
          )
        );
      } else {
        console.log("errorVideoData: ", errorVideoStatus);
      }
    } else if (alertPopup.type === "delete") {
      // Backend for removing it from database
      const {
        loading: loadingVideoDelete,
        error: errorVideoDelete,
        data: videoDelete,
      } = await deleteVideo({
        variables: {
          videoUuid,
        },
      });
      if (!loadingVideoDelete && videoDelete && videoDelete.deleteVideo) {
        // Remove video from list
        setVideos(prevVideos =>
          prevVideos.filter(video => video.id !== alertPopup.videoId)
        );
      } else {
        console.log("errorVideoDelete: ", errorVideoDelete);
      }
    }
    setAlertPopup({ open: false, type: "", videoId: null });
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", videoId: null });
  };

  // Popup handlers
  const handlePublishClick = videoId => {
    setAlertPopup({
      open: true,
      type: "publish",
      videoId: videoId,
    });
  };

  const handleUnpublishClick = videoId => {
    setAlertPopup({
      open: true,
      type: "unpublish",
      videoId: videoId,
    });
  };

  const handleDeleteClick = videoId => {
    setAlertPopup({
      open: true,
      type: "delete",
      videoId: videoId,
    });
  };

  return (
    <RootDiv>
      <title>R3PLAY - Videos Management – Admin</title>
      <ContainerDiv>
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
              position: "relative",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            // Firefox-specific rendering fix
            "@-moz-document url-prefix()": {
              "& .MuiDataGrid-columnHeaders": {
                transform: "translateY(0.1px)",
              },
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 12,
            },
          }}
          columns={columns}
          rows={rows}
          showCellRightBorder={true}
          slots={{ toolbar: DataGridTitle }}
          getRowId={row => row.id}
          autoHeight={true}
          rowHeight={90}
          experimentalFeatures={{ newEditingApi: true }}
          pagination
          pageSizeOptions={[10, 20, 50]} // user can select page size
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } }, // default to 10 rows
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          slotProps={{
            pagination: {
              SelectProps: {
                open: isPaginationMenuOpen,
                onOpen: () => setIsPaginationMenuOpen(true),
                onClose: () => setIsPaginationMenuOpen(false),
                ref: paginationSelectRef,
                MenuProps: {
                  PaperProps: {
                    ref: paginationMenuRef,
                  },
                },
              },
            },
          }}
        />

        {/* Alert Popup */}
        <AlertPopup
          open={alertPopup.open}
          onClose={handleCancelAction}
          title={
            alertPopup.type === "publish"
              ? "Draft to Publish"
              : alertPopup.type === "unpublish"
              ? "Publish to Draft"
              : "Delete Video"
          }
          message={
            alertPopup.type === "publish"
              ? "Are you ready to publish this video?"
              : alertPopup.type === "unpublish"
              ? "Are you sure you want to unpublish this video?"
              : "Are you sure you want to delete this video?"
          }
          confirmText={
            alertPopup.type === "publish"
              ? "YES, PUBLISH"
              : alertPopup.type === "unpublish"
              ? "YES, UNPUBLISH"
              : "YES, DELETE"
          }
          onConfirm={handleConfirmAction}
        />
      </ContainerDiv>
    </RootDiv>
  );
}
